<script setup>
import {ref} from 'vue'
import {Plus, ZoomIn, Delete} from '@element-plus/icons-vue'
import {userTokenStore} from "@/store/store.js";
import {useSnowflakeHook} from "@/hooks/snowflake.js";
import {getSignatureUrl, getSignatureVideoUrl} from "@/api/upload/upload.js";
import axios from 'axios';

const { generateId } = useSnowflakeHook();

const props = defineProps({
  // 文件最大上传数量
  limit: {
    type: Number,
    default: 5
  },
  // 图片宽度
  width: {
    type: Number,
    default: 900
  },
  // 图片高度
  height: {
    type: Number,
    default: 400
  },
  // 图片大小：MB
  fileSize: {
    type: Number,
    default: 1
  },
  // 文件格式
  fileType: {
    type: Array,
    default: () => ['mp4', 'mov']
  },
  // 提示出现位置，默认情况下是document.body，可以参考ElementPlus的Message组件文档
  tipsAppendTo: {
    type: String,
    default: "document.body"
  },
  // 外部索引，主要用于成功事件携带外部索引值，提供同个页面多个上传组件的索引值给外部赋值使用
  index: {
    type: Number,
    default: null
  },
  // 阿里oss预上传地址枚举
  aliOssPath: {
    required: true,
    type: String,
  },
  // 阿里云oss视频封面预上传地址枚举
  aliOssCoverPath: {
    required: true,
    type: String,
  },
  // 是否不限制尺寸
  isIgnoreWH: {
    type: Boolean,
    default: false
  },
  // 是否有视频
  isVideo: {
    type: Boolean,
    default: false
  }
})
// 事件
const emit = defineEmits(['success', 'change', 'remove', 'progress'])

/*文件上传请求配置*/
// 请求基地址
const baseUrl = import.meta.env.VITE_API_URL + "/upload/uploadImgs"
// token
const token = userTokenStore().token
// 请求头携带信息
const headers = {
  Authorization: token.tokenValue,
  "Login-Type": token.loginType
}
// 请求体携带信息
const data = {
  // 1临时文件 2永久文件
  uploadDownloadType: 2,
}

/*组件功能定义*/
// 是否有新上传文件（反显时配合提交功能使用）
const isChange = ref(false)
// 用于展示的文件列表
const fileList = ref([])
// 用于储存视频文件列表
const videoList = ref([])
// 用于对话框展示的大图地址
const dialogImageUrl = ref('')
// 展示对话框开启状态
const dialogVisible = ref(false)
// 组件锚点
const fileUpload = ref()
// 展示大图
const handleOnPreview = (file) => {
  dialogImageUrl.value = file.url
  dialogVisible.value = true
}
// 删除展示的文件
const handleOnRemove = (file) => {
  console.log("过滤之前", successUrl.value)
  successUrl.value = []
  successCoverUrl.value = []
  fileList.value = fileList.value.filter(item => {
    if (item.uid !== file.uid) {
      successUrl.value.push(item.url)
      successCoverUrl.value.push(item.coverUrl)
    }
    return item.uid !== file.uid
  })
  console.log("移除了文件", successUrl.value)
  emit('remove', successUrl.value, successCoverUrl.value, props.index)
  successLimit.value--
  successCoverLimit.value--
}
// 文件上传成功
const handleOnSuccess = (file) => {
  try {
    // 上传成功的文件修改上传状态
    const target = fileList.value.find(item => item.uid === file.uid)
    if (target) {
      target.status = "success"
    }
    console.log("file", file)
    successUrl.value.push(file.data[0]);
    successCoverUrl.value.push(file.coverData[0]);
    console.log("successUrl.value: ", successUrl.value);
    console.log("successCoverUrl.value: ", successCoverUrl.value);
    successLimit.value++;
    successCoverLimit.value++
    let resParam = []
    successUrl.value.forEach((item, index) => {
      let param = {
        coverImage: successCoverUrl.value[index],
        video: item,
        videoDurationSeconds: file.videoDurationSeconds
      }
      resParam.push(param)
    })
    if (successLimit.value === fileList.value.length) {
      console.log("resParam", resParam)
      emit("success", resParam, props.index);
    }
  } catch (e) {
    console.log(e);
    ElMessage({
      message: file.msg ? file.msg : "未知错误",
      type: "error",
      appendTo: props.tipsAppendTo,
    });
  }
}
// 文件上传失败
const handleOnError = () => {
  console.log("run in handleOnError")
  ElMessage({
    message: "上传失败，请重试",
    type: "error",
    appendTo: props.tipsAppendTo
  })
}
// 文件上传中
const handleProgress = () => {
  console.log("run in handleProgress")
  emit('progress')
}
// 选择了上传文件，使用自定义接口上传
const handleOnChange = async (file) => {
  const input = fileUpload.value?.$el?.querySelector('input[type="file"]')
  const files = input?.files
  if (files.length > 30) {
    ElMessage.error("单次上传最多选择30张图片！")
    fileList.value = fileList.value.filter(f => f.status === "success" && !f.url.startsWith("blob:"))
    successUrl.value = successUrl.value.filter(url => !url.startsWith("blob:"))
    successLimit.value = successUrl.value.length
    successCoverUrl.value = successCoverUrl.value.filter(url => !url.startsWith("blob:"))
    successCoverLimit.value = successCoverUrl.value.length
    return
  }
  isChange.value = true;
  const isPassFormat = await checkFileFormat(file);
  if (!isPassFormat) return
  const res = await uploadVideoFile(file)
  console.log("res", res)
  if (res.code === 0) {
    let param = {
      ...res.fileInfo,
      data: res.fileUrl,
      coverData: [],
      videoDurationSeconds: 0
    }
    // 是视频，生成封面图，并且上传
    if (file.raw.type.startsWith('video')) {
      const cover = await getVideoCover(file.raw)
      console.log("cover", cover)
      let coverStream = {
        raw: cover.coverFile,
        blob: URL.createObjectURL(cover.coverFile)
      }
      const coverRes = await uploadFile(coverStream)
      param.coverData = coverRes.fileUrl
      param.videoDurationSeconds = Math.floor(cover.duration)
      file.coverUrl = coverRes.fileUrl
      handleOnSuccess(param)
    } else {
      handleOnSuccess(param)
    }
  } else {
    ElMessage.error("文件上传失败")
  }
  // emit('change', fileList.value.length)
}
// 文件上传格式、大小错误
const handleOnExceed = () => {
  ElMessage({
    message: `上传文件数量不能超过 ${props.limit} 个!`,
    type: "error",
    appendTo: props.tipsAppendTo
  })
}
// 校验文件类型和文件大小
const checkFileFormat = async (file) => {
  // 校检文件类型
  if (props.fileType) {
    const fileName = file.name.split('.');
    const fileExt = fileName[fileName.length - 1];
    const isTypeOk = props.fileType.indexOf(fileExt) >= 0;
    if (!isTypeOk) {
      ElMessage({
        message: `文件格式不正确, 请上传${props.fileType.join("/")}格式文件!`,
        type: "error",
        appendTo: props.tipsAppendTo
      });
      const index = fileList.value.indexOf(file.uid);
      fileList.value.splice(index,1)
      return false;
    }
  }
  // 校检文件大小
  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      ElMessage({
        message: `上传文件大小不能超过 ${props.fileSize} MB!`,
        type: "error",
        appendTo: props.tipsAppendTo
      });
      const index = fileList.value.indexOf(file.uid);
      fileList.value.splice(index,1)
      return false;
    }
  }
  return true;
}
// 文件上传
const handleUpload = () => {
  fileUpload.value.submit()
}

/*反显功能*/
// 成功上传的文件数量（反显时配合提交功能使用）
const successLimit = ref(0)
// 成功上传的文件地址（反显时配合提交功能使用）
const successUrl = ref([])
// 成功上传的封面图文件数量（反显时配合提交功能使用）
const successCoverLimit = ref(0)
// 成功上传的封面图（反显时配合提交功能使用）
const successCoverUrl = ref([])
// 反显图片、视频
const handleReview = (reviewList) => {
  console.log("handleReview", reviewList)
  fileList.value = []
  reviewList.forEach(item => {
    fileList.value.push({url: item.video, coverUrl: item.coverImage, videoDurationSeconds: item.videoDurationSeconds})
    successUrl.value.push(item.video)
    successCoverUrl.value.push(item.coverImage)
  })
  successLimit.value = fileList.value.length
  successCoverLimit.value = fileList.value.length
}
// 使用预上传地址上传文件
const uploadFile = async (file) => {
  const urlParam = await getFileUploadUrl(file)
  if (urlParam.code === -1) return ElMessage.error("上传失败")
  const urlObj = new URL(urlParam.signedUrl);
  const fileUrl = urlObj.origin + urlObj.pathname;

  try {
    const response = await axios.put(urlParam.signedUrl, file.raw, {
      headers: { 'Content-Type': 'application/octet-stream' }
    });

    if (response.status === 200) {
      return { code: 0, fileUrl: [fileUrl], fileInfo: file };
    } else {
      return { code: -1, fileUrl: null };
    }

  } catch (err) {
    console.error(err);
    return { code: -1, fileUrl: null };
  }
}
// 使用预上传地址上传视频文件
const uploadVideoFile = async (file) => {
  const urlParam = await getVideoFileUploadUrl(file)
  if (urlParam.code === -1) return ElMessage.error("上传失败")
  const urlObj = new URL(urlParam.signedUrl);
  const fileUrl = urlObj.origin + urlObj.pathname;

  try {
    const response = await axios.put(urlParam.signedUrl, file.raw, {
      headers: { 'Content-Type': 'application/octet-stream' }
    });

    if (response.status === 200) {
      return { code: 0, fileUrl: [fileUrl], fileInfo: file };
    } else {
      return { code: -1, fileUrl: null };
    }

  } catch (err) {
    console.error(err);
    return { code: -1, fileUrl: null };
  }
}
// 获取文件预上传地址
const getFileUploadUrl = async (file) => {
  try {
    const id = generateId()
    const rawFile = file.raw
    // 获取文件后缀
    const ext = rawFile.name.includes('.') ? rawFile.name.substring(rawFile.name.lastIndexOf('.')) : '.bin';
    let param = {
      uploadDownloadTypeEnum: "PERMANENT",
      aliOssImgUploadPathEnum: props.aliOssCoverPath,
      fileName: props.aliOssCoverPath + id + ext
    }
    const res = await getSignatureUrl(param)
    console.log("res", res)
    return {code: 0, signedUrl: res}
  } catch (e) {
    console.log(e)
    return {code: -1, signedUrl: null}
  }
}
// 获取视频文件预上传地址
const getVideoFileUploadUrl = async (file) => {
  try {
    const id = generateId()
    const rawFile = file.raw
    // 获取文件后缀
    const ext = rawFile.name.includes('.') ? rawFile.name.substring(rawFile.name.lastIndexOf('.')) : '.bin';
    let param = {
      uploadDownloadTypeEnum: "PERMANENT",
      aliOssVideoUploadPathEnum: props.aliOssPath,
      fileName: props.aliOssPath + id + ext
    }
    const res = await getSignatureVideoUrl(param)
    console.log("res", res)
    return {code: 0, signedUrl: res}
  } catch (e) {
    console.log(e)
    return {code: -1, signedUrl: null}
  }
}
// 制作视频封面图
const getVideoCover = (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const videoUrl = URL.createObjectURL(videoFile)

    video.src = videoUrl
    video.muted = true
    video.playsInline = true
    video.crossOrigin = 'anonymous'

    let meta = {}

    // ① 先等元数据
    video.onloadedmetadata = () => {
      meta = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      }

      // 一定在这里再设置 currentTime
      video.currentTime = Math.min(0.2, video.duration || 0.2)
    }

    // ② 等 seek 真正完成
    video.onseeked = () => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      // ③ 用 toBlob 生成真正的二进制图片
      canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(videoUrl)

            if (!blob) {
              reject(new Error('封面生成失败'))
              return
            }

            const coverFile = new File(
                [blob],
                videoFile.name.replace(/\.\w+$/, '.jpg'),
                {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                }
            )

            resolve({coverFile, ...meta})
          },
          'image/jpeg',
          0.92
      )
    }

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl)
      reject(new Error('视频加载失败'))
    }
  })
}


// 暴露给外部使用的变量以及函数
defineExpose({
  handleUpload,
  handleReview,
  fileList,
  successUrl,
  successCoverUrl,
  isChange
})
</script>

<template>
  <el-upload
      style="text-align: left"
      v-model:file-list="fileList"
      :action="baseUrl"
      :multiple="true"
      :on-preview="handleOnPreview"
      :on-remove="handleOnRemove"
      :on-success="handleOnSuccess"
      :on-error="handleOnError"
      :on-progress="handleProgress"
      :on-exceed="handleOnExceed"
      :before-upload="checkFileFormat"
      :on-change="handleOnChange"
      list-type="picture-card"
      :limit="props.limit"
      :headers="headers"
      :data="data"
      ref="fileUpload"
      :auto-upload="false">
    <el-icon><Plus /></el-icon>

    <template #file="{ file }">
      <div class="w-100">
        <label class="success-icon">
          <i class="el-icon el-icon--upload-success el-icon--check icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
              <path
                  fill="currentColor"
                  d="M406.656 706.944 195.84 496.256a32 32 0 1 0-45.248 45.248l256 256 512-512a32 32 0 0 0-45.248-45.248L406.592 706.944z"
              ></path>
            </svg>
          </i>
        </label>
        <img class="el-upload-list__item-thumbnail w-100" :src="file.coverUrl" alt="" />
        <span class="el-upload-list__item-actions">
          <span
              class="el-upload-list__item-preview"
              @click="handleOnPreview(file)"
          >
            <el-icon><zoom-in /></el-icon>
          </span>
          <span
              class="el-upload-list__item-delete"
              @click="handleOnRemove(file)"
          >
            <el-icon><Delete /></el-icon>
          </span>
        </span>
      </div>
    </template>

    <template #tip>
      <div class="el-upload__tip">
        <span v-show="!props.isIgnoreWH">建议尺寸{{props.width}} * {{props.height}}</span>
        <span v-show="props.isIgnoreWH">尺寸不限制</span>
        ，最多{{props.limit}}<span>{{props.isVideo ? "个" : "张"}}</span>，单张图片不超过{{props.fileSize}}MB
      </div>
    </template>
  </el-upload>

  <el-dialog v-model="dialogVisible">
    <div>
      <img v-if="!props.isVideo" width="100%" :src="dialogImageUrl" alt="Preview Image" />
      <video muted controls class="w-100" v-else :src="dialogImageUrl">
        <source :src="dialogImageUrl" type="video/mp4" />
        你的浏览器不支持 video 标签
      </video>
    </div>
  </el-dialog>
</template>

<style scoped>
.success-icon {
  position: absolute;
  background: var(--el-color-success);
  height: 24px;
  right: -15px;
  text-align: center;
  top: -6px;
  transform: rotate(45deg);
  width: 40px;
  line-height: 10px;
  display: flex;
  justify-content: center;
  .icon {
    position: absolute;
    bottom: 0;
    transform: rotate(320deg);
  }
}
</style>

